import { Controller } from "@hotwired/stimulus";

// Drives the public /claim search front door.
//
// We do NOT use Google's classic Autocomplete widget (the one that injects a
// .pac-container into <body>). Instead we call the new Places API directly
// (AutocompleteSuggestion.fetchAutocompleteSuggestions) and render a fully
// custom dropdown that matches our DaisyUI / Tailwind look — primary text with
// matched ranges highlighted, secondary address line muted underneath,
// keyboard nav, ARIA combobox semantics.
//
// On selection we resolve the prediction's lat/lng via Place.fetchFields and
// load nearby listings into the #claim_results turbo-frame (the controller
// sets the frame's `src`; Turbo fetches + swaps). A skeleton shows while the
// frame loads.

const DEBOUNCE_MS = 200;

export default class extends Controller {
  static targets = [
    "input",
    "frame",
    "skeleton",
    "clear",
    "listbox",
    "dropdown",
    "addressOutput",
    "latitudeOutput",
    "longitudeOutput",
    // Structured form mode (e.g. the location editor): instead of a single
    // formatted-address string, the picked place is split into these editable
    // fields. Present only when the host form needs structured columns.
    "street1",
    "street2",
    "city",
    "state",
    "postalCode",
    "country",
  ];
  static values = {
    resultsUrl: String,
    country: { type: String, default: "us" },
    mode: { type: String, default: "search" },
  };

  connect() {
    // Snapshot the initial frame content ("why claim" intro) so the clear
    // button can restore it after a search.
    if (this.hasFrameTarget) {
      this.initialFrameHtml = this.frameTarget.innerHTML;
    }

    this.predictions = [];
    this.activeIndex = -1;
    this.fetchSeq = 0;
    this.lastQuery = "";
    this.sessionToken = null;
    this.debounceHandle = null;

    this.toggleClear();

    this.onDocumentPointerDown = (event) => this.handleOutsideClick(event);
    document.addEventListener("pointerdown", this.onDocumentPointerDown);

    if (window.google?.maps?.places) {
      this.initializePlaces();
    } else {
      this.boundInit = () => this.initializePlaces();
      window.addEventListener("google-maps-loaded", this.boundInit);
    }
  }

  disconnect() {
    if (this.boundInit) {
      window.removeEventListener("google-maps-loaded", this.boundInit);
    }
    document.removeEventListener("pointerdown", this.onDocumentPointerDown);
    if (this.debounceHandle) clearTimeout(this.debounceHandle);
  }

  initializePlaces() {
    this.mintSessionToken();
  }

  // One session token spans typing + the final selection's fetchFields; mint a
  // fresh one after each selection so each "search → pick" pair is billed as
  // one session by Google.
  mintSessionToken() {
    const Token = window.google?.maps?.places?.AutocompleteSessionToken;
    this.sessionToken = Token ? new Token() : null;
  }

  // === Input lifecycle ===========================================

  onInput() {
    this.toggleClear();
    const query = this.inputTarget.value.trim();

    if (this.debounceHandle) clearTimeout(this.debounceHandle);

    if (query === "") {
      this.predictions = [];
      this.activeIndex = -1;
      this.renderList();
      this.closeList();
      return;
    }

    if (query === this.lastQuery) return;

    this.debounceHandle = setTimeout(
      () => this.fetchPredictions(query),
      DEBOUNCE_MS,
    );
  }

  onFocus() {
    if (this.predictions.length > 0) this.openList();
    // Pull the input to the top of the viewport so suggestions have room above
    // the on-screen keyboard on mobile instead of being hidden behind it.
    this.inputTarget.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  onKeyDown(event) {
    const key = event.key;

    if (key === "Enter") {
      // Don't submit a parent form before a suggestion is chosen.
      event.preventDefault();
      const pick = this.predictions[this.activeIndex];
      if (pick) this.selectPrediction(pick);
      return;
    }

    if (key === "Escape") {
      event.preventDefault();
      this.closeList();
      return;
    }

    if (this.predictions.length === 0) return;

    if (key === "ArrowDown") {
      event.preventDefault();
      this.setActive((this.activeIndex + 1) % this.predictions.length);
    } else if (key === "ArrowUp") {
      event.preventDefault();
      const next =
        this.activeIndex <= 0
          ? this.predictions.length - 1
          : this.activeIndex - 1;
      this.setActive(next);
    }
  }

  // === Google Places fetch =======================================

  async fetchPredictions(query) {
    this.lastQuery = query;
    const Suggest = window.google?.maps?.places?.AutocompleteSuggestion;
    if (!Suggest?.fetchAutocompleteSuggestions) return;

    const seq = ++this.fetchSeq;

    try {
      const { suggestions } = await Suggest.fetchAutocompleteSuggestions({
        input: query,
        sessionToken: this.sessionToken,
        includedRegionCodes: [this.countryValue],
      });

      // Ignore stale responses if the user kept typing.
      if (seq !== this.fetchSeq) return;

      this.predictions = suggestions
        .map((s) => s.placePrediction)
        .filter(Boolean);
      this.activeIndex = -1;
      this.renderList();
      this.openList();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Places autocomplete error:", err);
      this.predictions = [];
      this.renderList();
      this.closeList();
    }
  }

  async selectPrediction(prediction) {
    try {
      const place = prediction.toPlace();
      const fields = ["location", "formattedAddress", "displayName"];
      // Only pay for address components when a structured form needs them.
      if (this.hasStreet1Target) fields.push("addressComponents");
      await place.fetchFields({ fields });

      const loc = place.location;
      const lat = typeof loc?.lat === "function" ? loc.lat() : loc?.lat;
      const lng = typeof loc?.lng === "function" ? loc.lng() : loc?.lng;
      if (lat == null || lng == null) return;

      const address =
        place.formattedAddress ||
        place.displayName ||
        prediction.text?.text ||
        "";

      this.inputTarget.value = address;
      this.toggleClear();
      this.closeList();
      // Drop focus so mobile keyboards dismiss after picking a suggestion
      // (onRowMouseDown suppresses the default blur to keep the tap alive).
      this.inputTarget.blur();
      this.lastQuery = address;

      // Session token convention: rotate after each completed selection.
      this.mintSessionToken();

      if (this.modeValue === "form") {
        if (this.hasStreet1Target) {
          this.writeStructured(place, lat, lng);
        } else {
          this.writeOutputs(lat, lng, address);
        }
      } else {
        this.search(lat, lng, address);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Place fetch error:", err);
    }
  }

  // === Dropdown rendering / ARIA =================================

  renderList() {
    if (!this.hasListboxTarget) return;

    if (this.predictions.length === 0) {
      this.listboxTarget.innerHTML = "";
      return;
    }

    this.listboxTarget.innerHTML = this.predictions
      .map((prediction, i) => this.rowMarkup(prediction, i))
      .join("");
  }

  rowMarkup(prediction, i) {
    const isActive = i === this.activeIndex;
    const primary = this.escapeHtml(
      prediction.mainText?.text || prediction.text?.text || "",
    );
    const secondary = prediction.secondaryText?.text
      ? this.escapeHtml(prediction.secondaryText.text)
      : "";
    const baseRow =
      "flex items-start gap-2.5 px-2 py-1.5 cursor-pointer rounded-lg transition-colors";
    const stateRow = isActive ? "bg-primary/10" : "hover:bg-base-200";

    return `
      <li id="claim-search-option-${i}"
          role="option"
          aria-selected="${isActive}"
          data-index="${i}"
          data-action="mousedown->claim-address-search#onRowMouseDown click->claim-address-search#onRowClick mousemove->claim-address-search#onRowHover"
          class="${baseRow} ${stateRow}">
        <span class="shrink-0 mt-0.5 text-base-content/40">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
            <path d="M20 10c0 7-8 13-8 13s-8-6-8-13a8 8 0 0 1 16 0Z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
        </span>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-semibold text-base-content leading-snug truncate">${primary}</p>
          ${secondary ? `<p class="text-xs text-base-content/60 leading-snug truncate mt-0.5">${secondary}</p>` : ""}
        </div>
      </li>
    `;
  }

  escapeHtml(str) {
    return String(str).replace(
      /[&<>"']/g,
      (c) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[c],
    );
  }

  setActive(index) {
    this.activeIndex = index;
    this.renderList();
    const id = `claim-search-option-${index}`;
    this.inputTarget.setAttribute("aria-activedescendant", id);
    document.getElementById(id)?.scrollIntoView({ block: "nearest" });
  }

  openList() {
    if (this.predictions.length === 0) {
      this.closeList();
      return;
    }
    this.dropdownTarget?.classList.remove("hidden");
    this.inputTarget.setAttribute("aria-expanded", "true");
  }

  closeList() {
    this.dropdownTarget?.classList.add("hidden");
    this.inputTarget?.setAttribute("aria-expanded", "false");
    this.inputTarget?.removeAttribute("aria-activedescendant");
  }

  // === Row events ================================================

  // Prevent the input from blurring before the click resolves.
  onRowMouseDown(event) {
    event.preventDefault();
  }

  onRowClick(event) {
    const i = parseInt(event.currentTarget.dataset.index, 10);
    const pick = this.predictions[i];
    if (pick) this.selectPrediction(pick);
  }

  onRowHover(event) {
    const i = parseInt(event.currentTarget.dataset.index, 10);
    if (Number.isFinite(i) && i !== this.activeIndex) this.setActive(i);
  }

  handleOutsideClick(event) {
    if (this.element.contains(event.target)) return;
    this.closeList();
  }

  // === Form-mode output (populates hidden fields) ================

  writeOutputs(lat, lng, address) {
    if (this.hasLatitudeOutputTarget) this.latitudeOutputTarget.value = lat;
    if (this.hasLongitudeOutputTarget) this.longitudeOutputTarget.value = lng;
    if (this.hasAddressOutputTarget) this.addressOutputTarget.value = address;
  }

  // Splits the selected place's address components into the structured fields
  // (street, city, state, postal, country) plus lat/lng. The fields stay
  // editable; this just pre-fills them from the pick.
  writeStructured(place, lat, lng) {
    const parts = {};
    (place.addressComponents || []).forEach((component) => {
      const type = component.types?.[0];
      if (type) {
        parts[type] = { long: component.longText || "", short: component.shortText || "" };
      }
    });
    const get = (type, key = "long") => (parts[type] ? parts[type][key] : "");

    if (this.hasStreet1Target) {
      this.street1Target.value = `${get("street_number")} ${get("route")}`.trim();
    }
    if (this.hasStreet2Target) this.street2Target.value = get("subpremise");
    if (this.hasCityTarget) {
      this.cityTarget.value =
        get("locality") || get("sublocality") || get("administrative_area_level_2");
    }
    if (this.hasStateTarget) {
      this.stateTarget.value = get("administrative_area_level_1", "short");
    }
    if (this.hasPostalCodeTarget) this.postalCodeTarget.value = get("postal_code");
    if (this.hasCountryTarget) this.countryTarget.value = get("country", "short");
    if (this.hasLatitudeOutputTarget) this.latitudeOutputTarget.value = lat;
    if (this.hasLongitudeOutputTarget) this.longitudeOutputTarget.value = lng;
  }

  clearOutputs() {
    if (this.hasLatitudeOutputTarget) this.latitudeOutputTarget.value = "";
    if (this.hasLongitudeOutputTarget) this.longitudeOutputTarget.value = "";
    if (this.hasAddressOutputTarget) this.addressOutputTarget.value = "";
  }

  // === Existing UI plumbing ======================================

  clear() {
    this.inputTarget.value = "";
    this.predictions = [];
    this.activeIndex = -1;
    this.lastQuery = "";
    this.renderList();
    this.closeList();

    if (this.modeValue === "form") {
      // Structured mode keeps the editable address fields; only the single-string
      // claim/org-submission outputs are reset here.
      if (!this.hasStreet1Target) this.clearOutputs();
    } else {
      this.hideSkeleton();
      if (this.hasFrameTarget) {
        this.frameTarget.removeAttribute("src");
        this.frameTarget.innerHTML = this.initialFrameHtml;
      }
    }

    this.toggleClear();
    this.inputTarget.focus();
  }

  toggleClear() {
    if (!this.hasClearTarget) return;
    this.clearTarget.classList.toggle(
      "hidden",
      this.inputTarget.value.trim() === "",
    );
  }

  search(lat, lng, address) {
    const url = new URL(this.resultsUrlValue, window.location.origin);
    url.searchParams.set("lat", lat);
    url.searchParams.set("lng", lng);
    if (address) url.searchParams.set("address", address);

    this.showSkeleton();
    // Set the attribute (not the .src property): Turbo observes the `src`
    // attribute to fetch + swap the frame.
    this.frameTarget.setAttribute("src", url.toString());
  }

  // Fired by turbo:frame-load on the results frame.
  onResults() {
    this.hideSkeleton();
  }

  showSkeleton() {
    if (this.hasSkeletonTarget) this.skeletonTarget.classList.remove("hidden");
    this.frameTarget.classList.add("hidden");
  }

  hideSkeleton() {
    if (this.hasSkeletonTarget) this.skeletonTarget.classList.add("hidden");
    this.frameTarget.classList.remove("hidden");
  }
}
